/** "Enable notifications" — subscribes this device to Web Push (VAPID). Staff only; shows state + test button. */
import React, { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';

async function api(path, method = 'GET', body) {
  const r = await fetch(`${API_URL}/api${path}`, { method, credentials: 'include', headers: body ? { 'Content-Type': 'application/json' } : undefined, body: body ? JSON.stringify(body) : undefined });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(formatApiError(d) || 'Request failed');
  return d;
}
const keyBytes = (key) => { const pad = '='.repeat((4 - (key.length % 4)) % 4); const raw = atob((key + pad).replace(/-/g, '+').replace(/_/g, '/')); return Uint8Array.from(raw, (c) => c.charCodeAt(0)); };
const supported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export function PushToggle({ compact = false }) {
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const iosNeedsInstall = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.navigator.standalone;

  const check = async () => {
    if (!supported()) return setState('unsupported');
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub && Notification.permission === 'granted') {
        const st = await api('/push/status');
        setState(st.endpoints.includes(sub.endpoint) ? 'on' : 'off');
      } else setState(Notification.permission === 'denied' ? 'denied' : 'off');
    } catch { setState('off'); }
  };
  useEffect(() => { check(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'denied' : 'off'); toast.error('Notifications were not allowed'); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const { publicKey, enabled } = await api('/push/config');
      if (!enabled) throw new Error('Push is not configured on the server');
      let sub = await reg.pushManager.getSubscription();
      if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
      await api('/push/subscribe', 'POST', { ...sub.toJSON(), device: navigator.userAgent.slice(0, 200) });
      setState('on');
      toast.success('Notifications enabled on this device');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) { await api('/push/unsubscribe', 'POST', { endpoint: sub.endpoint }); await sub.unsubscribe(); }
      setState('off'); toast.success('Notifications turned off on this device');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const test = async () => { try { await api('/push/test', 'POST', {}); toast.success('Test notification sent'); } catch (e) { toast.error(e.message); } };

  if (state === 'loading') return null;
  if (state === 'unsupported') return compact ? null : <p className="text-xs text-gray-500" data-testid="push-unsupported">{iosNeedsInstall ? 'On iPhone, add the portal to your Home Screen (Share → Add to Home Screen) to get notifications.' : 'This browser doesn\'t support notifications.'}</p>;
  if (state === 'on') {
    return (
      <div className="flex items-center gap-2 text-xs" data-testid="push-on">
        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><BellRing className="w-3.5 h-3.5" /> Notifications on</span>
        <button onClick={test} className="underline text-gray-600" data-testid="push-test">Send test</button>
        <button onClick={disable} disabled={busy} className="underline text-gray-500" data-testid="push-disable">Turn off</button>
      </div>
    );
  }
  if (state === 'denied') return <span className="text-xs text-red-600 inline-flex items-center gap-1" data-testid="push-denied"><BellOff className="w-3.5 h-3.5" /> Notifications blocked — allow them in your browser's site settings</span>;
  return (
    <Button size="sm" variant="outline" onClick={enable} disabled={busy} className="border-amber-400 text-amber-900 bg-amber-50 hover:bg-amber-100 font-semibold" data-testid="push-enable">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Bell className="w-4 h-4 mr-1.5" /> Enable notifications</>}
    </Button>
  );
}
